import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { downloadBlob } from '@/app/helpers/downloadBlob';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { useNotificationStreamOpen } from '../../notifications/core/streamStatus';
import { taskKeys } from './_consts';
import { clearFinishedTasks, dismissTask, fetchTaskFile, listMyTasks } from './_requests';
import { countUnseen, isRunning, latestEndTime, readSeenUntil, writeSeenUntil } from './_utils';

type Task = TaskInfoDto<string>;

/** Referência estável: `data` indefinido não pode virar um array novo a cada render (dependência de efeitos). */
const NO_TASKS: Task[] = [];

const POLL_WITHOUT_STREAM_MS = 3_000;
/** Com o stream de pé a lista quase não é consultada: só cobre evento perdido, que o SSE não reentrega. */
const POLL_WITH_STREAM_MS = 15_000;

type MyTasksOptions = {
  /**
   * Consulta em intervalo enquanto houver tarefa rodando. No React Query cada observador tem o próprio timer:
   * só o header liga, senão sino, aba e modal multiplicariam as consultas.
   */
  poll?: boolean;
  enabled?: boolean;
};

/**
 * Tarefas do usuário. A lista do servidor é a verdade; o evento SSE `task` (no `NotificationsContext`) só
 * acelera, escrevendo neste mesmo cache. Sem stream, a lista se atualiza sozinha enquanto houver tarefa rodando.
 */
export const useMyTasks = ({ poll = false, enabled = true }: MyTasksOptions = {}) => {
  const streamOpen = useNotificationStreamOpen();
  return useQuery({
    queryKey: taskKeys.mine,
    queryFn: listMyTasks,
    enabled,
    refetchInterval: (query) => {
      if (!poll || !query.state.data?.some(isRunning)) return false;
      return streamOpen ? POLL_WITH_STREAM_MS : POLL_WITHOUT_STREAM_MS;
    },
    // Falha ao listar não derruba o header: a aba Tarefas mostra o erro.
    meta: { suppressGlobalError: true },
  });
};

/** Uma tarefa do usuário, observada no mesmo cache da aba Tarefas. Sem nome não há o que consultar. */
export const useTask = (name: string | undefined) =>
  useMyTasks({ enabled: !!name }).data?.find((task) => task.name === name);

/** O que o sino precisa saber: o que roda, o que terminou sem ser visto e como marcar como visto. */
export const useTaskNews = () => {
  const { data: tasks = NO_TASKS } = useMyTasks({ poll: true });
  const [seenUntil, setSeenUntil] = useState(readSeenUntil);

  const running = tasks.filter(isRunning);
  const unseen = countUnseen(tasks, seenUntil);

  const markSeen = useCallback(() => {
    // Nunca anda para trás: dispensar a finalizada mais recente não ressuscita as anteriores.
    const latest = Math.max(readSeenUntil(), latestEndTime(tasks));
    writeSeenUntil(latest);
    setSeenUntil(latest);
  }, [tasks]);

  return { tasks, running, unseen, hasNews: running.length > 0 || unseen > 0, markSeen };
};

export const useDismissTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: Task) => dismissTask(task.name),
    onSuccess: (_response, task) => {
      queryClient.setQueryData<Task[]>(taskKeys.mine, (tasks) => tasks?.filter(({ name }) => name !== task.name));
    },
    onError: (error) => SystemNotification.error(getErrorMessage(error)),
  });
};

export const useClearFinishedTasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearFinishedTasks,
    onSuccess: () => queryClient.setQueryData<Task[]>(taskKeys.mine, (tasks) => tasks?.filter(isRunning)),
    onError: (error) => SystemNotification.error(getErrorMessage(error)),
  });
};

/** Baixa o arquivo da exportação concluída com o nome que o backend deu a ele. */
export const useDownloadTaskFile = () =>
  useMutation({
    mutationFn: async (task: Task) => downloadBlob(await fetchTaskFile(task), task.file_name ?? task.name),
    onError: (error) => SystemNotification.error(getErrorMessage(error)),
  });
